/**
 * The view header (PRD §2 "Header", redesign § View page).
 *
 * Two variants by mode: Metrics / Feed / Profile get the kit's sticky translucent pill, fixed to
 * the 960px content column; Posts gets a full-width flat bar (hairline bottom border) that
 * additionally hosts the search input, the Columns picker and Export — the old Posts toolbar. The
 * mode switcher is plain text and absolutely centered, so it never shifts as the right-side
 * controls change. A quiet sub-row under either variant shows post count, last scrape and the
 * group members.
 *
 * The left block identifies the subject in **every** mode, the flat bar included (the sub-row is
 * hidden there): a profile's avatar or a group's member-count badge, then the name over a quiet
 * meta line — `"7,281 followers • <note>"` for a profile, the members' summed followers for a
 * group. Every value is a server-formatted `#23` field and each half is omitted when absent, so a
 * pending summary renders exactly the header this file had before them.
 *
 * The right-side actions render in one fixed sequence — posts controls (search, Columns, Export),
 * Counted, Sort, Filter, Time — which produces every per-mode order: Metrics gets Counted → Filter
 * → Time, Posts gets Search → Columns → Export → Sort → Filter → Time, Feed gets Sort → Filter →
 * Time. Filter, Counted and the Posts controls are icon-only (named via aria-label, which the Menu
 * also surfaces as a hover title); Counted keeps its count badge so the size of the set stays
 * visible. Sort and Time carry labels: Sort always shows the direction arrow plus the active field
 * name (`SortMenu`, shared with `/feed` and `/saved`) — in Feed mode nothing else on screen says
 * what the ordering is.
 *
 * Scope rules come straight from the PRD: Filter and Date Range apply to Metrics, Posts and Feed;
 * Sort applies to Posts and Feed; Profile mode ignores all three. All three write into the
 * per-subject UI state, so they persist across navigation and reloads (§12.4). The Counted control
 * is the odd one out — metrics-only, and it writes the *global* setting (§5.1 #21), not UI state.
 *
 * `sort`/`onSortChange` are **mode-resolved** by the page: Posts and Feed persist separate sorts
 * (`sort` / `feedSort`, §12.3), and this component only ever sees the one belonging to the mode it
 * is rendering. The Export links read the same prop, which is correct because they mount only in
 * posts mode.
 */

import { Link } from 'react-router';
import {
  VIEW_MODES,
  getPostsColumn,
  type PostType,
  type SubjectType,
  type ViewFilterParams,
  type ViewFiltersState,
  type ViewMemberDTO,
  type ViewMode,
  type ViewRangeState,
  type ViewSummaryResponse,
} from '@lw/shared';
import { viewPostsExportUrl } from '../../api/views';
import { HOME_PATH } from '../../app/routes';
import {
  Avatar,
  CalendarIcon,
  ChevronLeftIcon,
  ColumnsIcon,
  DownloadIcon,
  FilterIcon,
  Menu,
  MenuItem,
  MenuSeparator,
  SearchIcon,
  cx,
} from '../../components';
import { formatDate, formatDateCompact } from '../../lib/datetime';
import { pluralize } from '../../lib/format';
import { VIEW_MODE_LABEL } from '../../lib/labels';
import { CountedTypesMenu } from './CountedTypesMenu';
import { FilterPanel } from './FilterPanel';
import { RangePanel } from './RangePanel';
import { SortMenu } from './SortMenu';
import { useColumnLayout } from './posts/useColumnLayout';
import {
  RANGE_PRESET_LABEL,
  activeFilterCount,
  type ResolvedRange,
  type ViewSort,
} from './viewState';

/** Action-button glyph size — sits optically level with the 13px trigger label. */
const ACTION_ICON = 13;

type PostsControlsProps = {
  subjectType: SubjectType;
  id: number;
  isGroup: boolean;
  sort: ViewSort;
  search: string;
  onSearchChange: (next: string) => void;
  postsParams: Partial<ViewFilterParams>;
};

/**
 * The old Posts toolbar, relocated into the flat bar: search, Columns, Export. Mounted only in
 * posts mode so the `columns:posts` ui-state read stays a posts-only request. The layout hook is
 * cache-backed, so this instance and the table's own instance always agree (see the hook).
 */
function PostsControls({
  subjectType,
  id,
  isGroup,
  sort,
  search,
  onSearchChange,
  postsParams,
}: PostsControlsProps) {
  const layout = useColumnLayout();
  const visibleColumns = layout.order.filter(
    (columnId) =>
      !layout.hidden.has(columnId) && (getPostsColumn(columnId).groupOnly ? isGroup : true),
  );

  const exportUrl = (format: 'csv' | 'json') =>
    viewPostsExportUrl(subjectType, id, {
      ...postsParams,
      sort: sort.key,
      dir: sort.dir,
      format,
      columns: format === 'csv' ? visibleColumns : undefined,
    });

  return (
    <>
      <div className="view-search">
        <SearchIcon size={14} />
        <input
          className="view-search__input"
          type="search"
          placeholder={isGroup ? 'Search post text or author…' : 'Search post text…'}
          aria-label="Search posts"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <Menu
        label={<ColumnsIcon size={ACTION_ICON} />}
        ariaLabel="Columns"
        align="right"
        triggerClassName="table-action table-action--icon"
        panelClassName="menu__panel--wide"
      >
        {() => (
          <>
            <div className="menu__checklist">
              {layout.order
                .filter((columnId) => (getPostsColumn(columnId).groupOnly ? isGroup : true))
                .map((columnId) => (
                  <label className="checklist__item" key={columnId}>
                    <input
                      type="checkbox"
                      checked={!layout.hidden.has(columnId)}
                      onChange={() => layout.toggle(columnId)}
                    />
                    <span className="checklist__label">{getPostsColumn(columnId).label}</span>
                  </label>
                ))}
            </div>
            <MenuSeparator />
            <MenuItem onClick={layout.reset}>Reset to defaults</MenuItem>
            <MenuItem disabled>
              {layout.pinned === null
                ? 'Drag a header to reorder'
                : `Drag a header to reorder · ${getPostsColumn(layout.pinned).label} stays first`}
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        label={<DownloadIcon size={ACTION_ICON} />}
        ariaLabel="Export"
        align="right"
        triggerClassName="table-action table-action--icon"
      >
        {(close) => (
          <>
            <a className="menu__item" href={exportUrl('csv')} onClick={close} download>
              CSV — visible columns
            </a>
            <a className="menu__item" href={exportUrl('json')} onClick={close} download>
              JSON — full post objects
            </a>
          </>
        )}
      </Menu>
    </>
  );
}

export type ViewHeaderProps = {
  subjectType: SubjectType;
  id: number;
  summary: ViewSummaryResponse | undefined;
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  filters: ViewFiltersState;
  onFiltersChange: (next: ViewFiltersState) => void;
  range: ViewRangeState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  sort: ViewSort;
  onSortChange: (next: ViewSort) => void;
  countedPostTypes: PostType[];
  /** `settings.mediaEnabled` — the avatar falls back to initials while it is off (§9.3). */
  mediaEnabled: boolean;
  timezone: string;
  now: number;
  /** Posts flat bar only: the debounced search input state (owned by the page). */
  search: string;
  onSearchChange: (next: string) => void;
  /** Posts flat bar only: the §5.2 filter params (with `q`) the Export links serialize. */
  postsParams: Partial<ViewFilterParams>;
};

export function ViewHeader({
  subjectType,
  id,
  summary,
  mode,
  onModeChange,
  filters,
  onFiltersChange,
  range,
  resolvedRange,
  onRangeChange,
  sort,
  onSortChange,
  countedPostTypes,
  mediaEnabled,
  timezone,
  now,
  search,
  onSearchChange,
  postsParams,
}: ViewHeaderProps) {
  const isGroup = subjectType === 'group';
  const isPosts = mode === 'posts';
  const members: ViewMemberDTO[] = summary?.members ?? [];
  const filterCount = activeFilterCount(filters, isGroup);
  const showFilterAndRange = mode !== 'profile';
  const showSort = mode === 'posts' || mode === 'feed';

  // The identity line under the name, per kind: a profile's followers and note, a group's summed
  // followers. Both halves are server-formatted strings; the labels and the separator are the only
  // words this file owns (§12.5). Absent fields (a pending summary, a server without them yet)
  // simply produce no line, which degrades to the name-only header.
  const followers = isGroup ? summary?.totalFollowers : summary?.followers;
  const note = isGroup ? null : summary?.notes ?? null;
  const metaParts: string[] = [];
  if (followers !== undefined && followers.value !== null) {
    metaParts.push(`${followers.display} followers`);
  }
  if (note !== null && note.trim().length > 0) metaParts.push(note.trim());
  const metaLine = metaParts.join(' • ');

  // A custom range labels its trigger with the actual resolved bounds (redesign § Date range).
  const rangeLabel =
    range.preset === 'custom'
      ? `${formatDateCompact(resolvedRange.from, timezone)} – ${formatDateCompact(resolvedRange.to, timezone)}`
      : RANGE_PRESET_LABEL[range.preset];

  return (
    <>
      <div className={cx('view-header', isPosts && 'view-header--flat')}>
        <div className={isPosts ? 'view-header__bar' : 'view-header__pill'}>
          <div className="view-header__left">
            <Link className="icon-btn" to={HOME_PATH} aria-label="Back to profiles">
              <ChevronLeftIcon />
            </Link>

            {/* Same 28px circle either way: a group has no picture, so it wears its size. */}
            {isGroup ? (
              summary?.memberCount === undefined ? null : (
                <span
                  className="view-header__count-badge"
                  title={pluralize(summary.memberCount, 'member')}
                >
                  {summary.memberCount}
                </span>
              )
            ) : (
              <Avatar
                media={summary?.avatar ?? null}
                name={summary?.name ?? null}
                mediaEnabled={mediaEnabled}
                size="sm"
                kind={summary?.kind ?? 'person'}
              />
            )}

            <div className="view-header__ident">
              <div className="view-header__title">
                <span className="view-header__name">{summary?.name ?? '…'}</span>
                {summary?.archived === true ? <span className="tag">Archived</span> : null}
              </div>
              {metaLine.length > 0 ? (
                <span className="view-header__meta" title={metaLine}>
                  {metaLine}
                </span>
              ) : null}
            </div>
          </div>

          <nav className="view-header__modes" aria-label="View mode">
            {VIEW_MODES.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={cx('mode-tab', candidate === mode && 'is-active')}
                aria-current={candidate === mode ? 'page' : undefined}
                onClick={() => onModeChange(candidate)}
              >
                {VIEW_MODE_LABEL[candidate]}
              </button>
            ))}
          </nav>

          <div className="view-header__actions">
            {isPosts ? (
              <PostsControls
                subjectType={subjectType}
                id={id}
                isGroup={isGroup}
                sort={sort}
                search={search}
                onSearchChange={onSearchChange}
                postsParams={postsParams}
              />
            ) : null}

            {/* Counted types move baselines, so the control sits where those numbers are shown. */}
            {mode === 'metrics' ? <CountedTypesMenu /> : null}

            {showSort ? <SortMenu sort={sort} onChange={onSortChange} isGroup={isGroup} /> : null}

            {showFilterAndRange ? (
              <Menu
                label={<FilterIcon size={ACTION_ICON} />}
                ariaLabel="Filter"
                align="right"
                triggerClassName="table-action table-action--icon"
                panelClassName="menu__panel--pad menu__panel--filter"
                badge={filterCount > 0 ? <span className="count-badge">{filterCount}</span> : null}
              >
                {() => (
                  <FilterPanel
                    filters={filters}
                    onChange={onFiltersChange}
                    isGroup={isGroup}
                    members={members}
                    countedPostTypes={countedPostTypes}
                  />
                )}
              </Menu>
            ) : null}

            {showFilterAndRange ? (
              <Menu
                label={
                  <>
                    <CalendarIcon size={ACTION_ICON} />
                    <span className="table-action__label">{rangeLabel}</span>
                  </>
                }
                align="right"
                panelClassName="menu__panel--pad"
              >
                {() => (
                  <RangePanel
                    range={range}
                    resolved={resolvedRange}
                    onChange={onRangeChange}
                    timezone={timezone}
                    now={now}
                  />
                )}
              </Menu>
            ) : null}
          </div>
        </div>
      </div>

      {/* Posts mode is fullscreen and carries these facts in its own footer instead. */}
      {summary !== undefined && !isPosts ? (
        <div className="view-sub">
          <span>{pluralize(summary.postCount, 'post')} stored</span>
          <span className="view-sub__dot" aria-hidden="true">
            ·
          </span>
          <span>
            {summary.lastScrapedAt === null
              ? 'Never scraped'
              : `Last scraped ${formatDate(summary.lastScrapedAt, timezone)}`}
          </span>
          {isGroup && members.length > 0 ? (
            <>
              <span className="view-sub__dot" aria-hidden="true">
                ·
              </span>
              <span className="view-sub__members">
                {members.map((member) => (
                  <span className="tag" key={member.id}>
                    {member.name ?? `#${member.id}`}
                  </span>
                ))}
              </span>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default ViewHeader;
